import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Grid,
  Paper,
  Tooltip,
  InputAdornment,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import FtpConfigSection from './FtpConfigSection';
import {
  ContentCopy,
  Refresh,
  Visibility,
  VisibilityOff,
  Code,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { ingestionService, stationService } from '../services/api';
import SensorMetricsReference from './SensorMetricsReference';

interface StationIngestionInfoProps {
  stationUuid: string;
  apiKey?: string;
}

const DATASTREAM_IDS = [
  // Weather
  { measure: 'temperature_celsius', datastreamId: 2 },
  { measure: 'rain_mm', datastreamId: 3 },
  { measure: 'solar_radiation_watt_per_sqm', datastreamId: 4 },
  { measure: 'wind_speed_m_per_s', datastreamId: 5 },
  { measure: 'wind_direction_deg', datastreamId: 6 },
  { measure: 'wind_gust_m_per_s', datastreamId: 7 },
  { measure: 'atmospheric_pressure_mbar', datastreamId: 9 },
  { measure: 'rh_percentage', datastreamId: 10 },
  { measure: 'reference_et_mm', datastreamId: 11 },
  { measure: 'uv_index', datastreamId: 12 },
  { measure: 'dew_point_celsius', datastreamId: 13 },
  { measure: 'visibility_km', datastreamId: 14 },
  // Air Quality
  { measure: 'pm1_ugm3', datastreamId: 15 },
  { measure: 'pm25_ugm3', datastreamId: 16 },
  { measure: 'pm10_ugm3', datastreamId: 17 },
  { measure: 'co_ppm', datastreamId: 18 },
  { measure: 'co2_ppm', datastreamId: 19 },
  { measure: 'no2_ugm3', datastreamId: 20 },
  { measure: 'no_ugm3', datastreamId: 21 },
  { measure: 'nox_ugm3', datastreamId: 22 },
  { measure: 'so2_ugm3', datastreamId: 23 },
  { measure: 'o3_ugm3', datastreamId: 24 },
  { measure: 'voc_ugm3', datastreamId: 25 },
  { measure: 'nh3_ugm3', datastreamId: 26 },
  // Agriculture
  { measure: 'soil_temperature_10cm_celsius', datastreamId: 27 },
  { measure: 'soil_temperature_20cm_celsius', datastreamId: 28 },
  { measure: 'soil_temperature_30cm_celsius', datastreamId: 29 },
  { measure: 'soil_temperature_50cm_celsius', datastreamId: 30 },
  { measure: 'soil_temperature_100cm_celsius', datastreamId: 31 },
  { measure: 'soil_moisture_10cm_percent', datastreamId: 32 },
  { measure: 'soil_moisture_20cm_percent', datastreamId: 33 },
  { measure: 'soil_moisture_30cm_percent', datastreamId: 34 },
  { measure: 'soil_moisture_50cm_percent', datastreamId: 35 },
  { measure: 'soil_moisture_100cm_percent', datastreamId: 36 },
  { measure: 'soil_ec_dsm', datastreamId: 37 },
  { measure: 'soil_ph', datastreamId: 38 },
  { measure: 'leaf_wetness_percent', datastreamId: 39 },
  { measure: 'leaf_temperature_celsius', datastreamId: 40 },
  // Water Quality
  { measure: 'water_temperature_celsius', datastreamId: 41 },
  { measure: 'water_ph', datastreamId: 42 },
  { measure: 'water_do_mgl', datastreamId: 43 },
  { measure: 'water_ec_usm', datastreamId: 44 },
  { measure: 'water_turbidity_ntu', datastreamId: 45 },
  { measure: 'water_level_m', datastreamId: 46 },
  { measure: 'water_flow_m3s', datastreamId: 47 },
  // Noise
  { measure: 'noise_level_db', datastreamId: 48 },
  { measure: 'noise_peak_db', datastreamId: 49 },
  { measure: 'light_level_lux', datastreamId: 50 },
  // Smart City
  { measure: 'vehicle_count', datastreamId: 51 },
  { measure: 'vehicle_speed_avg_kmh', datastreamId: 52 },
  { measure: 'pedestrian_count', datastreamId: 53 },
  { measure: 'parking_occupancy_percent', datastreamId: 54 },
  { measure: 'road_surface_temp_celsius', datastreamId: 55 },
  { measure: 'traffic_density_veh_per_km', datastreamId: 56 },
];

const StationIngestionInfo: React.FC<StationIngestionInfoProps> = ({ stationUuid, apiKey }) => {
  // Static ingestion info — no API call needed (same for all stations)
  const [ingestionInfo] = useState<any>({
    example_payload: {
      temperature_celsius: 24.5,
      rh_percentage: 58.2,
      atmospheric_pressure_mbar: 1013.25,
      wind_speed_m_per_s: 3.2,
      wind_direction_deg: 180,
      rain_mm: 0.0,
      solar_radiation_watt_per_sqm: 850,
      reference_et_mm: 0.3,
      pm25_ugm3: 12.0,
      soil_moisture_10cm_percent: 32.0,
      water_ph: 7.2,
      noise_level_db: 55.0,
      vehicle_count: 150,
    }
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [protocolTab, setProtocolTab] = useState(0);

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copied to clipboard!` });
  };

  const handleRegenerateApiKey = async () => {
    if (!confirm('Are you sure you want to regenerate the API key? The old key will no longer work.')) {
      return;
    }

    setLoading(true);
    try {
      const result = await stationService.regenerateApiKey(stationUuid);
      setSnackbar({ open: true, message: 'API key regenerated successfully! Refresh the page to see the new key.' });
      // Refresh the parent component or notify it somehow
    } catch (error) {
      console.error('Error regenerating API key:', error);
      setSnackbar({ open: true, message: 'Failed to regenerate API key' });
    } finally {
      setLoading(false);
    }
  };

  const displayKey = showApiKey ? (apiKey || '<your_api_key>') : '<your_api_key>';

  const getEndpointUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/v1/ingest/${displayKey}/`;
  };

  const getCurlExample = () => {
    const endpoint = getEndpointUrl();
    const payload = JSON.stringify({
      readings: {
        temperature: 25.5,
        humidity: 60.0,
        pressure: 1013.25
      }
    }, null, 2);

    return `curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-Timestamp: $(date -u +"%Y-%m-%dT%H:%M:%SZ")" \\
  -d '${payload}'`;
  };

  const getPythonExample = () => {
    const endpoint = getEndpointUrl();
    return `import requests
from datetime import datetime

endpoint = "${endpoint}"
headers = {
    "Content-Type": "application/json",
    "X-Timestamp": datetime.utcnow().isoformat() + "Z"
}

payload = {
    "readings": {
        "temperature": 25.5,
        "humidity": 60.0,
        "pressure": 1013.25,
        "wind_speed": 5.2,
        "wind_direction": 180
    }
}

response = requests.post(endpoint, json=payload, headers=headers)
print(f"Status: {response.status_code}")
print(f"Response: {response.json()}")`;
  };

  const getArduinoExample = () => {
    const endpoint = getEndpointUrl();
    return `#include <WiFi.h>
#include <HTTPClient.h>
#include <ArduinoJson.h>

const char* endpoint = "${endpoint}";

void sendSensorData(float temperature, float humidity) {
  if (WiFi.status() == WL_CONNECTED) {
    HTTPClient http;
    http.begin(endpoint);
    http.addHeader("Content-Type", "application/json");

    StaticJsonDocument<200> doc;
    JsonObject readings = doc.createNestedObject("readings");
    readings["temperature"] = temperature;
    readings["humidity"] = humidity;

    String payload;
    serializeJson(doc, payload);

    int httpCode = http.POST(payload);
    String response = http.getString();

    Serial.println("Status: " + String(httpCode));
    Serial.println("Response: " + response);

    http.end();
  }
}`;
  };

  const getStaBaseUrl = () => {
    return `${window.location.origin}/sensorthings/v1.1`;
  };

  const getStaCurlExample = () => {
    const baseUrl = getStaBaseUrl();
    return `# POST a new Observation (e.g. temperature_celsius, Datastream ID 2)
curl -X POST "${baseUrl}/Observations" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${displayKey}" \\
  -d '{
  "Datastream": {"@iot.id": 2},
  "phenomenonTime": "2025-11-21T12:00:00Z",
  "result": 25.5
}'

# GET all Datastreams
curl -H "Authorization: Bearer ${displayKey}" \\
  "${baseUrl}/Datastreams"

# GET Observations for a specific Datastream
curl -H "Authorization: Bearer ${displayKey}" \\
  "${baseUrl}/Datastreams(2)/Observations?\\$top=10&\\$orderby=phenomenonTime%20desc"`;
  };

  const getStaPythonExample = () => {
    const baseUrl = getStaBaseUrl();
    return `import requests
from datetime import datetime

base_url = "${baseUrl}"
headers = {
    "Content-Type": "application/json",
    "Authorization": "Bearer ${displayKey}"
}

# POST a new Observation (e.g. temperature_celsius, Datastream ID 2)
observation = {
    "Datastream": {"@iot.id": 2},
    "phenomenonTime": datetime.utcnow().isoformat() + "Z",
    "result": 25.5
}
response = requests.post(
    f"{base_url}/Observations",
    json=observation,
    headers=headers
)
print(f"POST Status: {response.status_code}")

# GET Datastreams
response = requests.get(f"{base_url}/Datastreams", headers=headers)
print(f"Datastreams: {response.json()}")

# GET Observations for a Datastream
response = requests.get(
    f"{base_url}/Datastreams(2)/Observations",
    params={"$top": 10, "$orderby": "phenomenonTime desc"},
    headers=headers
)
print(f"Observations: {response.json()}")`;
  };

  if (!apiKey) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Loading ingestion information...
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* API Key Section - Common across all tabs */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Code color="primary" />
            API Credentials
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="API Key"
                type={showApiKey ? 'text' : 'password'}
                value={apiKey || ''}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showApiKey ? 'Hide' : 'Show'}>
                        <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy API Key">
                        <IconButton
                          onClick={() => handleCopyToClipboard(apiKey || '', 'API Key')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerate API Key">
                        <IconButton
                          onClick={handleRegenerateApiKey}
                          disabled={loading}
                          edge="end"
                          color="warning"
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Keep your API key secure!</strong> Do not share it publicly or commit it to version control.
            </Typography>
          </Alert>
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>About API Key Regeneration:</strong> The API key identifies your station. If you regenerate it:
              <br/>• The old key will stop working immediately
              <br/>• You must update your sensor/device with the new key
              <br/>• Historical measurements are NOT affected - they remain in the database
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Protocol Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={protocolTab} onChange={(_, v) => setProtocolTab(v)}>
          <Tab label="HTTP" />
          <Tab label="MQTT" />
          <Tab label="OGC SensorThings" />
          <Tab label="FTP" />
        </Tabs>
      </Box>

      {/* Tab 0: HTTP */}
      <div role="tabpanel" hidden={protocolTab !== 0}>
        {protocolTab === 0 && (
          <Box>
            {/* HTTP Endpoint */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="HTTP Ingestion Endpoint"
                      type={showApiKey ? 'text' : 'password'}
                      value={getEndpointUrl()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Endpoint URL">
                              <IconButton
                                onClick={() => handleCopyToClipboard(getEndpointUrl(), 'Endpoint URL')}
                                edge="end"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Metrics Reference */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <SensorMetricsReference protocol="http" />
              </CardContent>
            </Card>

            {/* Example Payload */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Example Payload
                  </Typography>
                  <Tooltip title="Copy JSON">
                    <IconButton
                      onClick={() => handleCopyToClipboard(
                        JSON.stringify(ingestionInfo.example_payload, null, 2),
                        'Example JSON'
                      )}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                  <pre style={{
                    margin: 0,
                    color: '#d4d4d4',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '13px'
                  }}>
                    {JSON.stringify(ingestionInfo.example_payload, null, 2)}
                  </pre>
                </Paper>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Optional Header:</strong> X-Timestamp (ISO 8601 format or Unix timestamp)
                    <br />
                    If not provided, the server will use the current time.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            {/* Code Examples */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Code Examples
                </Typography>

                {/* cURL Example */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      cURL (Terminal)
                    </Typography>
                    <Tooltip title="Copy cURL command">
                      <IconButton onClick={() => handleCopyToClipboard(getCurlExample(), 'cURL command')}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                    <pre style={{
                      margin: 0,
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px'
                    }}>
                      {getCurlExample()}
                    </pre>
                  </Paper>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Python Example */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Python
                    </Typography>
                    <Tooltip title="Copy Python code">
                      <IconButton onClick={() => handleCopyToClipboard(getPythonExample(), 'Python code')}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                    <pre style={{
                      margin: 0,
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px'
                    }}>
                      {getPythonExample()}
                    </pre>
                  </Paper>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Arduino Example */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Arduino (ESP32/ESP8266)
                    </Typography>
                    <Tooltip title="Copy Arduino code">
                      <IconButton onClick={() => handleCopyToClipboard(getArduinoExample(), 'Arduino code')}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                    <pre style={{
                      margin: 0,
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px'
                    }}>
                      {getArduinoExample()}
                    </pre>
                  </Paper>
                </Box>
              </CardContent>
            </Card>

            {/* Expected Response */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle color="success" />
                  Expected Response
                </Typography>
                <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1 }}>
                  <pre style={{
                    margin: 0,
                    color: '#4ec9b0',
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    fontSize: '13px'
                  }}>
                    {JSON.stringify({
                      status: "success",
                      message: "Data ingested successfully"
                    }, null, 2)}
                  </pre>
                </Paper>
              </CardContent>
            </Card>
          </Box>
        )}
      </div>

      {/* Tab 1: MQTT */}
      <div role="tabpanel" hidden={protocolTab !== 1}>
        {protocolTab === 1 && (
          <Box>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Code color="primary" />
                  MQTT Configuration
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="MQTT Broker"
                      value={window.location.hostname}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Broker Address">
                              <IconButton
                                onClick={() => handleCopyToClipboard(window.location.hostname, 'MQTT Broker')}
                                edge="end"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="MQTT Port"
                      value="31883"
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Username"
                      value={`station_${stationUuid}`}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Username">
                              <IconButton
                                onClick={() => handleCopyToClipboard(`station_${stationUuid}`, 'Username')}
                                edge="end"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Password"
                      value="Same as API Key"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText="Use your API Key as the MQTT password"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="MQTT Topic"
                      value={`sensors/${stationUuid}`}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Topic">
                              <IconButton
                                onClick={() => handleCopyToClipboard(`sensors/${stationUuid}`, 'MQTT Topic')}
                                edge="end"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>How MQTT Works:</strong>
                    <br />• <strong>Username</strong>: <code>station_{'{uuid}'}</code> (shown above)
                    <br />• <strong>Password</strong>: Your API Key
                    <br />• <strong>Topic</strong>: <code>sensors/{'{uuid}'}</code> — you can only publish to your own topic
                    <br />• <strong>timestamp</strong> (in JSON): Required - format: YYYY/MM/DD HH:MM:SS
                    <br />• <strong>Message</strong>: JSON with API key, timestamp and measurements
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>Example Command:</strong>
                    <br />
                    <code style={{ display: 'block', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px', color: '#1e1e1e', fontFamily: 'Consolas, Monaco, "Courier New", monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {`mosquitto_pub -h ${window.location.hostname} -p 31883 \\\n  -u "station_${stationUuid}" -P "${displayKey}" \\\n  -t "sensors/${stationUuid}" \\\n  -m '{"api_key": "${displayKey}", "timestamp": "2025/11/21 12:00:00", "temperature": 25.5, "humidity": 60.0}'`}
                    </code>
                  </Typography>
                </Alert>
              </CardContent>
            </Card>

            {/* MQTT Metrics Reference */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <SensorMetricsReference protocol="mqtt" />
              </CardContent>
            </Card>
          </Box>
        )}
      </div>

      {/* Tab 2: OGC SensorThings */}
      <div role="tabpanel" hidden={protocolTab !== 2}>
        {protocolTab === 2 && (
          <Box>
            {/* Base URL */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Code color="primary" />
                  OGC SensorThings API v1.1
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="SensorThings Base URL"
                      value={getStaBaseUrl()}
                      fullWidth
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy Base URL">
                              <IconButton
                                onClick={() => handleCopyToClipboard(getStaBaseUrl(), 'SensorThings Base URL')}
                                edge="end"
                              >
                                <ContentCopy />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Datastream IDs Table */}
            <Card sx={{ mb: 3, background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
                  Datastream IDs
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
                  Each measure maps to a fixed Datastream ID. You can also discover them dynamically via <code style={{ color: '#00d4ff' }}>GET /Datastreams</code>.
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {DATASTREAM_IDS.map((row) => (
                    <Box key={row.datastreamId} sx={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      p: 1, mb: 0.5, borderRadius: 1, background: 'rgba(0,0,0,0.15)',
                    }}>
                      <code style={{ color: '#00d4ff', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                        {row.measure}
                      </code>
                      <Chip label={`ID: ${row.datastreamId}`} size="small"
                        sx={{ background: 'rgba(0,212,255,0.1)', color: '#00d4ff', fontFamily: 'monospace', fontSize: '0.8rem' }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Code Examples */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Code Examples
                </Typography>

                {/* cURL Example */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      cURL
                    </Typography>
                    <Tooltip title="Copy cURL examples">
                      <IconButton onClick={() => handleCopyToClipboard(getStaCurlExample(), 'SensorThings cURL examples')}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                    <pre style={{
                      margin: 0,
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px'
                    }}>
                      {getStaCurlExample()}
                    </pre>
                  </Paper>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Python Example */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Python
                    </Typography>
                    <Tooltip title="Copy Python code">
                      <IconButton onClick={() => handleCopyToClipboard(getStaPythonExample(), 'SensorThings Python code')}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
                    <pre style={{
                      margin: 0,
                      color: '#d4d4d4',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      fontSize: '12px'
                    }}>
                      {getStaPythonExample()}
                    </pre>
                  </Paper>
                </Box>
              </CardContent>
            </Card>

            {/* Rate Limit Info */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Rate Limit:</strong> 1 request per 240 seconds per station. This limit is shared with the HTTP ingestion endpoint.
              </Typography>
            </Alert>
          </Box>
        )}
      </div>

      {/* Tab 3: FTP Pull */}
      <div role="tabpanel" hidden={protocolTab !== 3}>
        {protocolTab === 3 && (
          <FtpConfigSection stationUuid={stationUuid} />
        )}
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default StationIngestionInfo;
